import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, Truck, Receipt, User, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import AttachmentsButton from "@/components/AttachmentsButton";
import { usePermissions } from "@/lib/permissions";
import CustomerProfilePanel from "@/components/orders/CustomerProfilePanel";
import QuickProductDialog from "@/components/orders/QuickProductDialog";
import ItemsTable from "@/components/orders/ItemsTable";
import IncentiveHint from "@/components/orders/IncentiveHint";

const STATUS = ["pending", "confirmed", "in_process", "dispatched", "delivered", "cancelled"];
const PAY    = ["unpaid", "advance", "partial", "paid"];
const DISP   = ["not_dispatched", "packed", "dispatched", "delivered"];

function OrderEditDialog({ order, customers, products, units, transports, sources,
                          teamMembers, canEditCustomer, onClose, onSaved, onProductsChanged, isAdmin }) {
  const locked = order.order_status === "delivered" && !isAdmin;
  const [customerId, setCustomerId] = useState(order.customer_id || "");
  const [items, setItems] = useState((order.items || []).map(it => ({
    product_id: it.product_id || "", name: it.name || "",
    qty: it.qty || 1, price: it.price || 0, gst: it.gst ?? 18,
    hsn: it.hsn || "", unit: it.unit || "PCS",
  })));
  const [orderStatus, setOrderStatus]     = useState(order.order_status);
  const [paymentStatus, setPaymentStatus] = useState(order.payment_status);
  const [dispatchStatus, setDispatchStatus] = useState(order.dispatch_status);
  const [tracking, setTracking]  = useState(order.tracking_number || "");
  const [transport, setTransport] = useState(order.transport || "");
  const [source, setSource]      = useState(order.source || "");
  const [assignedTo, setAssignedTo] = useState(order.assigned_to || "");
  const [quickCreateFor, setQuickCreateFor] = useState(null);

  const addItem = () => setItems([...items, { product_id: "", name: "", qty: 1, price: 0, gst: 18, hsn: "", unit: "PCS" }]);
  const total = items.reduce((s, it) => s + (it.qty || 0) * (it.price || 0) * (1 + (it.gst || 0) / 100), 0);

  const save = async () => {
    if (!items.length) return toast.error("Add at least one item");
    const cust = customers.find(c => c.id === customerId) || {};
    const patch = {
      order_status: orderStatus,
      payment_status: paymentStatus,
      dispatch_status: dispatchStatus,
      tracking_number: tracking,
      transport, source, assigned_to: assignedTo || null,
    };
    if (!locked) {
      patch.customer_id = customerId;
      patch.customer_name = cust.customer_name || order.customer_name;
      patch.items = items;
    }
    try {
      await api.put(`/orders/${order.id}`, patch);
      toast.success("Order updated"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="order-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Order · <span className="font-mono text-sm">{order.number}</span></DialogTitle>
        </DialogHeader>
        {locked && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200 text-xs p-3">
            This order is marked <b>delivered</b> — customer, items and status are locked. Only an Admin can re-open it.
          </div>
        )}
        <div className="grid md:grid-cols-[1fr_320px] gap-4">
          {/* LEFT: form */}
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">CUSTOMER</Label>
              <Select value={customerId} onValueChange={setCustomerId} disabled={locked}>
                <SelectTrigger data-testid="edit-order-customer"><SelectValue placeholder={order.customer_name} /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs">ITEMS</Label>
                {!locked && <Button size="sm" variant="outline" onClick={addItem} data-testid="edit-order-add-item"><Plus size={12} className="mr-1" />Item</Button>}
              </div>
              <ItemsTable items={items} products={products} units={units}
                onChange={setItems} disabled={locked}
                onQuickCreate={(idx) => setQuickCreateFor(idx)} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">ORDER STATUS</Label>
                <Select value={orderStatus} onValueChange={setOrderStatus} disabled={locked}>
                  <SelectTrigger data-testid="edit-order-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">PAYMENT</Label>
                <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                  <SelectTrigger data-testid="edit-order-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAY.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">DISPATCH</Label>
                <Select value={dispatchStatus} onValueChange={setDispatchStatus}>
                  <SelectTrigger data-testid="edit-order-dispatch"><SelectValue /></SelectTrigger>
                  <SelectContent>{DISP.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">TRANSPORT / COURIER</Label>
                <Select value={transport} onValueChange={setTransport}>
                  <SelectTrigger data-testid="edit-order-transport"><SelectValue placeholder="Pick transport" /></SelectTrigger>
                  <SelectContent>{transports.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">SOURCE</Label>
                <Select value={source} onValueChange={setSource}>
                  <SelectTrigger data-testid="edit-order-source"><SelectValue placeholder="Order source" /></SelectTrigger>
                  <SelectContent>{sources.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">ASSIGN TO</Label>
                <Select value={assignedTo || "__none__"} onValueChange={(v) => setAssignedTo(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="edit-order-assigned-to"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Unassigned</SelectItem>
                    {teamMembers.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">TRACKING #</Label><Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="AWB…" data-testid="edit-order-tracking" /></div>
              <div>
                <Label className="text-xs">CREATED BY</Label>
                <div className="h-10 px-3 flex items-center gap-2 rounded-md border border-border bg-secondary/40 text-sm text-muted-foreground" data-testid="edit-order-created-by">
                  <User size={12} weight="duotone" />
                  <span className="truncate">{order.created_by_name || "—"}</span>
                  <span className="ml-auto text-[10px] uppercase">read-only</span>
                </div>
              </div>
            </div>
            <div className="text-right font-heading font-bold text-xl" data-testid="edit-order-total">
              Total: ₹{total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </div>
            <IncentiveHint customerId={customerId} items={items} ownerId={order.owner_id} testId="edit-order-incentive" />
          </div>
          {/* RIGHT: customer profile side panel */}
          <div className="md:sticky md:top-2 md:h-fit">
            <CustomerProfilePanel customerId={customerId} canEditCustomer={canEditCustomer} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="edit-order-save">Save changes</Button>
        </DialogFooter>
        <QuickProductDialog
          open={quickCreateFor !== null}
          onOpenChange={(v) => !v && setQuickCreateFor(null)}
          units={units}
          onCreated={(p) => {
            onProductsChanged(p);
            setItems(items.map((it, idx) => idx === quickCreateFor
              ? { ...it, product_id: p.id, name: p.name, price: p.price, gst: p.gst, unit: p.unit || "PCS" }
              : it));
            setQuickCreateFor(null);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export default function Orders() {
  const { can, canEdit, canDelete, isAdmin } = usePermissions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [team, setTeam] = useState([]);
  const [transports, setTransports] = useState([]);
  const [units, setUnits] = useState([]);
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("");
  const [transport, setTransport] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [notes, setNotes] = useState("");
  const [quickCreateFor, setQuickCreateFor] = useState(null);

  const load = async () => {
    const safe = (p) => p.catch(() => ({ data: [] }));
    const [o, c, p, t, tr, un, sr] = await Promise.all([
      safe(api.get("/orders")),
      safe(api.get("/customers", { params: { scope_mode: "tenant" } })),
      can("inventory.view") ? safe(api.get("/products")) : Promise.resolve({ data: [] }),
      safe(api.get("/users")),
      safe(api.get("/lookups/courier")),
      safe(api.get("/lookups/product_unit")),
      safe(api.get("/lookups/order_source")),
    ]);
    setOrders(o.data); setCustomers(c.data); setProducts(p.data); setTeam(t.data);
    setTransports(tr.data); setUnits(un.data); setSources(sr.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const canEditCustomer = can("customers.edit");
  const addItem = () => setItems([...items, { product_id: "", name: "", qty: 1, price: 0, gst: 18, hsn: "", unit: "PCS" }]);

  const resetForm = () => {
    setCustomerId(""); setItems([]); setSource(""); setTransport("");
    setAssignedTo(""); setNotes("");
  };

  const create = async () => {
    const cust = customers.find(c => c.id === customerId);
    if (!cust) return toast.error("Pick a customer");
    if (!items.length) return toast.error("Add items");
    try {
      await api.post("/orders", {
        customer_id: cust.id, customer_name: cust.customer_name,
        items, source, transport,
        assigned_to: assignedTo || null,
        notes,
      });
      toast.success("Order created");
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const update = async (id, patch) => {
    try { await api.put(`/orders/${id}`, patch); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete order ${o.number}? It will move to the recycle bin.`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Order deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete not allowed"); }
  };

  const createShipment = async (o) => {
    if (!window.confirm(`Create a Delhivery shipment for ${o.number}?`)) return;
    try {
      const r = await api.post("/integrations/delhivery/create-shipment", { order_id: o.id });
      toast.success(`Shipment created · Waybill ${r.data.waybill || "—"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Shipment failed"); }
  };

  const generateInvoice = async (o) => {
    if (o.order_status === "cancelled") return toast.error("Cancelled orders cannot be invoiced");
    if (!window.confirm(`Generate an invoice for order ${o.number}?`)) return;
    try {
      const r = await api.post("/invoices/from-order", { order_id: o.id, due_days: 15 });
      toast.success(`Invoice ${r.data.number} created`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const total = items.reduce((s, it) => s + it.qty * it.price * (1 + (it.gst || 0) / 100), 0);

  // Transport label lookup for the list view
  const transportLabel = useMemo(() => {
    const m = {}; transports.forEach(t => { m[t.key] = t.label; }); return m;
  }, [transports]);

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex justify-between items-end gap-4">
        <div>
          <div className="overline text-muted-foreground mb-2">Fulfillment</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Orders</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          {can("orders.create") && (
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-order-button">
                <Plus size={16} className="mr-2" />New Order
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Order</DialogTitle></DialogHeader>
            <div className="grid md:grid-cols-[1fr_320px] gap-4">
              {/* LEFT: form */}
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-xs">CUSTOMER</Label>
                  <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger data-testid="order-customer-select"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs">ITEMS</Label>
                    <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-button"><Plus size={12} className="mr-1" />Item</Button>
                  </div>
                  <ItemsTable items={items} products={products} units={units}
                    onChange={setItems} onQuickCreate={(idx) => setQuickCreateFor(idx)} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">TRANSPORT / COURIER</Label>
                    <Select value={transport} onValueChange={setTransport}>
                      <SelectTrigger data-testid="order-transport"><SelectValue placeholder="Pick transport" /></SelectTrigger>
                      <SelectContent>{transports.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">SOURCE</Label>
                    <Select value={source} onValueChange={setSource}>
                      <SelectTrigger data-testid="order-source"><SelectValue placeholder="Order source" /></SelectTrigger>
                      <SelectContent>{sources.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">ASSIGN TO</Label>
                    <Select value={assignedTo || "__none__"} onValueChange={(v) => setAssignedTo(v === "__none__" ? "" : v)}>
                      <SelectTrigger data-testid="order-assigned-to"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {team.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">NOTES</Label>
                    <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" data-testid="order-notes" />
                  </div>
                  <div>
                    <Label className="text-xs">CREATED BY</Label>
                    <div className="h-10 px-3 flex items-center gap-2 rounded-md border border-border bg-secondary/40 text-sm text-muted-foreground" data-testid="order-created-by">
                      <User size={12} weight="duotone" />
                      <span className="truncate">{user?.name || user?.email}</span>
                      <span className="ml-auto text-[10px] uppercase">auto</span>
                    </div>
                  </div>
                </div>
                <div className="text-right font-heading font-bold text-xl" data-testid="order-total">
                  Total: ₹{total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </div>
                <IncentiveHint customerId={customerId} items={items} testId="new-order-incentive" />
              </div>
              {/* RIGHT: side panel */}
              <div className="md:sticky md:top-2 md:h-fit">
                <CustomerProfilePanel customerId={customerId} canEditCustomer={canEditCustomer} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} data-testid="save-order-button">Create Order</Button>
            </DialogFooter>
            <QuickProductDialog
              open={quickCreateFor !== null}
              onOpenChange={(v) => !v && setQuickCreateFor(null)}
              units={units}
              onCreated={(p) => {
                setProducts(prev => [p, ...prev]);
                setItems(items.map((it, idx) => idx === quickCreateFor
                  ? { ...it, product_id: p.id, name: p.name, price: p.price, gst: p.gst, unit: p.unit || "PCS" }
                  : it));
                setQuickCreateFor(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
              <tr>
                <th className="text-left p-4">Order #</th>
                <th className="text-left p-4">Customer</th>
                <th className="text-left p-4">Total</th>
                <th className="text-left p-4">Incentive</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Payment</th>
                <th className="text-left p-4">Dispatch</th>
                <th className="text-left p-4">Transport</th>
                <th className="text-left p-4">Assigned</th>
                <th className="text-left p-4">Created By</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-border/40" data-testid={`order-${o.id}`}>
                  <td className="p-4 font-mono text-xs">{o.number}</td>
                  <td className="p-4 font-medium">{o.customer_name}</td>
                  <td className="p-4 font-heading font-bold">₹{o.total?.toLocaleString("en-IN")}</td>
                  <td className="p-4" data-testid={`order-incentive-${o.id}`}>
                    {o.incentive_amount != null && o.incentive_amount > 0 ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/5 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:text-emerald-200"
                            title={o.incentive_rule_name || ""}>
                        {o.incentive_rate_pct != null ? `${o.incentive_rate_pct}%` : (o.incentive_structure === "fixed" ? "Flat" : "")}
                        <span className="text-emerald-900 dark:text-emerald-100">· ₹{Number(o.incentive_amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="p-4">
                    {canEdit("orders.edit", o) ? (
                      <Select value={o.order_status} onValueChange={(v) => update(o.id, { order_status: v })}>
                        <SelectTrigger className="h-8 text-xs w-36" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary capitalize" data-testid={`order-status-ro-${o.id}`}>{o.order_status}</span>
                    )}
                  </td>
                  <td className="p-4">
                    {canEdit("orders.edit", o) ? (
                      <Select value={o.payment_status} onValueChange={(v) => update(o.id, { payment_status: v })}>
                        <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>{PAY.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary capitalize">{o.payment_status}</span>
                    )}
                  </td>
                  <td className="p-4">
                    {canEdit("orders.edit", o) ? (
                      <Select value={o.dispatch_status} onValueChange={(v) => update(o.id, { dispatch_status: v })}>
                        <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>{DISP.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary capitalize">{o.dispatch_status}</span>
                    )}
                  </td>
                  <td className="p-4 text-xs" data-testid={`order-transport-${o.id}`}>
                    {transportLabel[o.transport] || o.courier || <span className="text-muted-foreground">—</span>}
                    {o.tracking_number && <div className="text-[10px] font-mono text-muted-foreground">{o.tracking_number}</div>}
                  </td>
                  <td className="p-4 text-xs" data-testid={`order-assigned-${o.id}`}>{o.assigned_to_name || <span className="text-muted-foreground">Unassigned</span>}</td>
                  <td className="p-4 text-xs text-muted-foreground" data-testid={`order-created-by-${o.id}`}>{o.created_by_name || "—"}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 justify-end">
                      {can("orders.view") && (
                        <button onClick={() => navigate(`/orders/${o.id}`)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="View order details" data-testid={`view-order-${o.id}`}><Eye size={14} weight="duotone" /></button>
                      )}
                      {canEdit("orders.edit", o) && (
                        <button onClick={() => setEditing(o)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Edit order" data-testid={`edit-order-${o.id}`}><PencilSimple size={14} weight="duotone" /></button>
                      )}
                      <AttachmentsButton entityType="order" entityId={o.id} label={`Files — ${o.number}`} testId={`attach-order-${o.id}`} />
                      {can("invoices.create") && o.order_status !== "cancelled" && (
                        <button onClick={() => generateInvoice(o)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Generate invoice" data-testid={`invoice-order-${o.id}`}><Receipt size={14} weight="duotone" /></button>
                      )}
                      {canEdit("orders.edit", o) && !o.tracking_number && (
                        <button onClick={() => createShipment(o)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Create Delhivery shipment" data-testid={`ship-order-${o.id}`}><Truck size={14} weight="duotone" /></button>
                      )}
                      {canDelete("orders.delete", o) && (
                        <button onClick={() => remove(o)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete" data-testid={`delete-order-${o.id}`}><Trash size={14} weight="duotone" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!orders.length && <tr><td colSpan={11} className="p-10 text-center text-muted-foreground">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <OrderEditDialog
          order={editing}
          customers={customers}
          products={products}
          units={units}
          transports={transports}
          sources={sources}
          teamMembers={team}
          canEditCustomer={canEditCustomer}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSaved={load}
          onProductsChanged={(p) => setProducts(prev => [p, ...prev])}
        />
      )}
    </div>
  );
}
